import NoPermissionsTooltipWrapper from "@src/lib/ui/NoPermissionsTooltipWrapper";
import Spinner from "@src/lib/ui/Spinner";
import Text from "@src/lib/ui/Text";
import { usePrevious } from "@src/lib/hooks/usePrevious";
import { cn } from "@src/lib/utils/cn";
import React, { FC, forwardRef, useCallback } from "react";
import TrashOutlineIcon from "@src/lib/icons/18px/trash-outline";
import XIcon from "@src/lib/icons/18px/x";
import { Separator } from "@src/lib/ui/Separator";

export type MultiSelectAction = {
  icon?: React.ReactElement;
  key: string;
  label?: string;
  hasPermission?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  component?: React.ReactNode;
};

// Ghost row action inside the banner: hover fill, Button-matching focus ring.
const MULTI_SELECT_BANNER_ACTION_CLASSNAME =
  "flex cursor-pointer select-none items-center rounded-lg px-2 py-1 text-text-primary hover:bg-state-hover active:bg-state-pressed focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand-border";

export type MultiSelectOnDestroy = undefined | (() => void);
export type Props = {
  numSelected: number;
  onDeselectAll: () => void;
  onDestroy?: MultiSelectOnDestroy;
  destroyLabel?: string;
  hasDestroyPermission?: boolean;
  actions?: MultiSelectAction[];
  hidden?: boolean;
  selectAll?: () => void;
  selectAllLoading?: boolean;
  totalItemCount?: number;
};

const MultiSelectBanner = forwardRef<HTMLDivElement, Props>(
  (
    {
      numSelected,
      onDeselectAll,
      actions,
      onDestroy,
      hasDestroyPermission = true,
      hidden,
      selectAll,
      totalItemCount,
      selectAllLoading,
      destroyLabel = "Remove",
    },
    ref,
  ) => {
    const previousSelectAllLoading = usePrevious(selectAllLoading);
    if (numSelected === 0 || hidden) {
      return null;
    }
    const selectAllLoadingComplete =
      previousSelectAllLoading && !selectAllLoading;
    return (
      <div
        className="fixed inset-x-0 bottom-8 z-[48] mx-auto max-w-[60%] rounded-xl border border-border-primary bg-bg-elevated text-text-primary shadow-large"
        ref={ref}
      >
        <div className="flex items-center p-3">
          <button
            type="button"
            aria-label="Deselect all"
            onClick={onDeselectAll}
            className={cn(
              MULTI_SELECT_BANNER_ACTION_CLASSNAME,
              "mr-1 size-7 justify-center p-0 text-icon-secondary hover:text-icon-primary",
            )}
          >
            <XIcon className="size-4" />
          </button>
          <Text variant="compact-title">{numSelected} selected</Text>
          <div className="ml-4 flex min-w-0 items-center overflow-hidden max-2xl:hidden">
            <Text
              variant="fine-print-description"
              className="text-text-tertiary"
            >
              <Text variant="fine-print-title" className="text-text-tertiary">
                Pro tip:&nbsp;
              </Text>
              Hold the
            </Text>
            <kbd className="mx-1 flex items-center rounded-md border border-border-primary bg-bg-secondary px-1 py-0.5 font-sans-v4 text-2xs-v4 text-text-secondary">
              shift
            </kbd>
            <Text
              variant="fine-print-description"
              className="text-text-tertiary"
            >
              key to select multiple rows with 1 click
            </Text>
          </div>
          {selectAll &&
            numSelected !== totalItemCount &&
            !selectAllLoadingComplete && (
              <div className="ml-auto flex shrink-0 cursor-pointer items-center pl-2">
                {selectAllLoading ? (
                  <Spinner size={16} style={{ marginRight: 8 }} />
                ) : (
                  <button
                    type="button"
                    onClick={selectAll}
                    className={cn(MULTI_SELECT_BANNER_ACTION_CLASSNAME, "px-3")}
                  >
                    <Text variant="compact-title" inline>
                      Select all {totalItemCount}
                    </Text>
                  </button>
                )}
              </div>
            )}
        </div>
        <Separator />
        <div className="flex items-center overflow-x-auto p-2">
          {actions && (
            <div className="flex items-center gap-3">
              {actions.length > 1 && (
                <div className="pl-1">
                  <Text variant="compact-description">Actions:</Text>
                </div>
              )}
              {actions.map(
                (action) =>
                  action.component || (
                    <MultiSelectBannerButton action={action} key={action.key} />
                  ),
              )}
            </div>
          )}
          {onDestroy ? (
            <NoPermissionsTooltipWrapper
              hasPermission={hasDestroyPermission}
              caretPosition="Top"
              tooltipSide="bottom"
            >
              <button
                type="button"
                aria-disabled={!hasDestroyPermission || undefined}
                className={cn(
                  MULTI_SELECT_BANNER_ACTION_CLASSNAME,
                  "ml-auto",
                  !hasDestroyPermission &&
                    "opacity-50 hover:bg-transparent active:bg-transparent",
                )}
                onClick={!hasDestroyPermission ? undefined : onDestroy}
              >
                <TrashOutlineIcon className="mr-1" />
                <Text variant="compact-title" inline>
                  {destroyLabel}
                </Text>
              </button>
            </NoPermissionsTooltipWrapper>
          ) : null}
        </div>
      </div>
    );
  },
);

MultiSelectBanner.displayName = "MultiSelectBanner";

export default MultiSelectBanner;

type MultiSelectBannerButtonProps = {
  action: MultiSelectAction;
};

export const MultiSelectBannerButton: FC<MultiSelectBannerButtonProps> = ({
  action,
}) => {
  const lacksPermission = action.hasPermission === false;
  const onClickCallback = useCallback(() => {
    if (action.disabled || lacksPermission) {
      return;
    }
    if (action.onClick) {
      action.onClick();
    }
  }, [action, lacksPermission]);
  const inactive = action.disabled || lacksPermission;
  return (
    <button
      type="button"
      key={action.key}
      aria-disabled={inactive || undefined}
      className={cn(
        MULTI_SELECT_BANNER_ACTION_CLASSNAME,
        "mr-1",
        inactive &&
          "cursor-default opacity-50 hover:bg-transparent active:bg-transparent",
      )}
      onClick={onClickCallback}
    >
      {action.icon && (
        <span className="mr-1 flex items-center justify-center">
          {action.icon}
        </span>
      )}
      <Text variant="compact-title" inline>
        {action.label}
      </Text>
    </button>
  );
};
